import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import { formatCurrency } from '@/libs/antd/formater';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { FleetGetListParams } from '@/libs/fleet-api/fetcher';
import {
	useMonitoringPlafondSearchCustomer,
	useMonitoringPlafondSearchOptions,
} from '@/libs/fleet-api/services/monitoring';
import { ArrowRightOutlined } from '@ant-design/icons';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	Progress,
	Row,
	Select,
	Table,
	Typography,
} from 'antd';
import { FC, Fragment, useState } from 'react';

const DEFAULT_PARAM: FleetGetListParams = {
	pagination: {
		pageNumber: 1,
		pageSize: 20,
	},
	search: {
		by: '',
		value: '',
	},
};
const MonitoringPlafondListPage: FC<FleetGetListParams> = (props) => {
	const { navigate } = useApp();
	const { openCustomerDetail, openCustomerGroupDetail } = useModals();

	const { form, fieldName } = useForm<FleetGetListParams>();
	const monitoringPlafondSearchOptionQuery =
		useMonitoringPlafondSearchOptions();
	const searchBy = Form.useWatch(fieldName(['search', 'by']), form);

	const [param, setParam] = useState<FleetGetListParams>({
		...DEFAULT_PARAM,
		...props,
	});

	const { searchByOptions = [] } =
		monitoringPlafondSearchOptionQuery.data?.data ?? {};
	const searchCustomerQuery = useMonitoringPlafondSearchCustomer(param);

	const { items, additionalData } = searchCustomerQuery.data?.data ?? {};

	const renderProgress = ({
		name,
		limit,
		total,
	}: {
		name: string;
		limit: number;
		total: number;
	}) => {
		return (
			<div style={{ marginBlock: 40 }}>
				<Row>
					<Col flex="auto">
						<strong>{name}</strong>
					</Col>
					<Col flex="none">{formatCurrency(total)}</Col>
				</Row>
				<Progress
					percent={total ? ((total - limit) / total) * 100 : 0}
					showInfo={false}
				/>
				<Row>
					<Col flex="auto">Sisa Limit:</Col>
					<Col flex="none">{limit ? formatCurrency(limit) : '-'}</Col>
				</Row>
			</div>
		);
	};

	return (
		<Fragment>
			<FleetTitle style={{ marginBottom: 20 }}>Monitoring Plafon</FleetTitle>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={param}
				layout="horizontal"
				colon={false}
				labelAlign="left"
				labelCol={{ style: { width: 160 } }}
				style={{ marginBlock: 20 }}
				onFinish={(values) => {
					setParam({
						...values,
						pagination: {
							pageNumber: 1,
							pageSize: 10,
						},
					});
				}}
			>
				<Row
					gutter={20}
					wrap={false}
				>
					<Col flex="none">
						<Form.Item
							label="Search by"
							name={fieldName(['search', 'by'])}
						>
							<Select
								allowClear
								style={{ width: 200 }}
								options={searchByOptions.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
								onSelect={() => {
									form.resetFields([fieldName(['search', 'value'])]);
								}}
								onClear={() => {
									form.resetFields([fieldName(['search'])]);
								}}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						<Form.Item
							name={fieldName(['search', 'value'])}
							noStyle
						>
							{searchBy === 'limit-type' ? (
								<Select
									style={{ width: '100%' }}
									placeholder="Pilih tipe limit"
									allowClear
									options={[
										{ value: '1', label: 'Non Join Financing' },
										{ value: '2', label: 'Joint Financing' },
									]}
								/>
							) : (
								<Input />
							)}
						</Form.Item>
					</Col>
					<Col flex="none">
						<Button
							block
							style={{ width: 200 }}
							type="primary"
							htmlType="submit"
							disabled={searchCustomerQuery.isFetching}
							loading={searchCustomerQuery.isLoading}
						>
							SEARCH
						</Button>
					</Col>
				</Row>
			</Form>

			<Divider />
			<Typography.Title level={3}>Informasi Limit Pembiayaan</Typography.Title>
			<Row gutter={40}>
				<Col span={12}>
					<Typography.Title level={3}>Non-Revolving</Typography.Title>
					{renderProgress({
						name: 'Total Limit Existing NJF',
						total: additionalData?.nonRevolvingNjfTotalLimitAmount ?? 0,
						limit: additionalData?.nonRevolvingNjfLimitExistingAmount ?? 0,
					})}
					{renderProgress({
						name: 'Total Limit Existing JF',
						total: additionalData?.nonRevolvingJfTotalLimitAmount ?? 0,
						limit: additionalData?.nonRevolvingJfLimitExistingAmount ?? 0,
					})}
				</Col>
				<Col span={12}>
					<Typography.Title level={3}>Revolving</Typography.Title>
					{renderProgress({
						name: 'Total Limit Existing NJF',
						total: additionalData?.revolvingNjfTotalLimitAmount ?? 0,
						limit: additionalData?.revolvingNjfLimitExistingAmount ?? 0,
					})}
					{renderProgress({
						name: 'Total Limit Existing JF',
						total: additionalData?.revolvingJfTotalLimitAmount ?? 0,
						limit: additionalData?.revolvingJfLimitExistingAmount ?? 0,
					})}
				</Col>
			</Row>

			<Divider />
			<Typography.Title level={3}>List Plafon</Typography.Title>

			<Table
				style={{ marginBlock: 20 }}
				bordered
				scroll={{ x: 'max-content' }}
				loading={searchCustomerQuery.isFetching}
				dataSource={items?.map((record, key) => {
					return {
						...record,
						key,
					};
				})}
				pagination={false}
				columns={[
					{
						title: 'No',
						align: 'center',
						fixed: 'left',
						width: 1,
						render: (_, __, index) => index + 1,
					},
					{
						title: 'Customer ID',
						render: (_, record) => record.customerId ?? '-',
					},
					{
						title: 'Nama Customer',
						render: (_, record) =>
							record.customerId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!record.customerId) return;
										openCustomerDetail(String(record.customerId));
									}}
								>
									{record.customerName ?? '-'}
								</a>
							) : (
								record.customerName ?? '-'
							),
					},
					{
						title: 'Group BMPP',
						render: (_, record) =>
							record.bmppGroupId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!record.bmppGroupId) return;
										openCustomerGroupDetail(record.bmppGroupId);
									}}
								>
									{record.bmppGroupName ?? '-'}
								</a>
							) : (
								record.bmppGroupName ?? '-'
							),
					},
					{
						title: 'Group BMPP',
						render: (_, record) => record.bmppGroupName ?? '-',
					},
					{
						title: 'Total Limit Pembiayaan',
						render: (_, record) =>
							formatCurrency(record.totalFinancingLimitAmount ?? 0),
					},
					{
						title: 'Action',
						fixed: 'right',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('MonitoringPlafondPage', {
											customerId: record.customerId,
											onBack: () => {
												navigate('MonitoringPlafondListPage', param);
											},
										});
									}}
								>
									<ArrowRightOutlined />
								</a>
							);
						},
					},
				]}
			/>
		</Fragment>
	);
};

export default MonitoringPlafondListPage;
